import { type ComponentProps, type ReactNode, forwardRef } from 'react';
import { cn } from '../lib/utils';

export interface InputProps extends ComponentProps<'input'> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

/**
 * This is a modified version of the shadcn Input which allows us to pass a ref
 * to the underlying input.  React 19 has automatic ref forwarding, and shadcn
 * relies on this, but since we're on React 18, we need to handle this ourselves.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftIcon, rightIcon, ...props }, ref) => {
    const baseClasses = cn(
      // Figma field fill is `background/surface` — not shadcn's translucent `dark:bg-input/30`.
      'text-text-primary shadow-xs flex h-9 w-full min-w-0 rounded border border-input bg-transparent px-3 py-2 body-md-strong outline-none transition-[color,box-shadow] selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-text-secondary disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-background-surface md:text-sm',
      'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
      'aria-invalid:ring-1 aria-invalid:ring-destructive aria-invalid:border-destructive',
    );
    if (leftIcon || rightIcon) {
      return (
        <div className={cn('relative', className)}>
          {leftIcon && (
            <div className="absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-primary">
              {leftIcon}
            </div>
          )}
          <input
            type={type}
            data-slot="input"
            className={cn(baseClasses, { 'pl-9': leftIcon, 'pr-9': rightIcon })}
            {...props}
            ref={ref}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-primary">
              {rightIcon}
            </div>
          )}
        </div>
      );
    }

    return (
      <input
        type={type}
        data-slot="input"
        className={cn(baseClasses, className)}
        {...props}
        ref={ref}
      />
    );
  },
);

Input.displayName = 'Input';
